// SPEC-UPDATE-TIPS-V1 (D-661): 更新を待つ間に出す「使い方の一行」。
//   ★読む側(ログイン画面)は anon。書く側(マネサポ)は admin。同じ表を扱うのでこの 1 か所にまとめる。
//   ★読み取りは絶対に投げない。更新作業の邪魔をしない、が最優先(spec DESIGN 四)。
//     取れなければ空配列。空配列なら画面は何も出さない。
import { supabase } from '../lib/supabase'

// 一度に読む上限。Tips は数十件を超える運用を想定していないが、上限は必ず置く(D-330 GUARD)。
export const TIPS_LIMIT = 100

/** 行 → 画面が使う形。★分からない値は埋めない(D-492)。 */
export function toTip(row) {
  return {
    tipId: row?.tip_id ?? null,
    body: typeof row?.body === 'string' ? row.body : '',
    isActive: row?.is_active !== false,
    sortOrder: Number.isFinite(Number(row?.sort_order)) ? Number(row.sort_order) : 0,
  }
}

/**
 * 画面に出す Tips(有効なものだけ)。
 *   ★失敗しても投げない・ログも画面も汚さない。戻り値は必ず配列。
 *   ★本文が空の行は出さない(管理画面で誤って空保存されても、現場に空行が出ない)。
 */
export async function fetchActiveTips() {
  try {
    const { data, error } = await supabase
      .from('app_tips')
      .select('tip_id, body, is_active, sort_order')
      .eq('is_active', true)
      .order('sort_order')
      .limit(TIPS_LIMIT)
    if (error) return []
    return (data ?? []).map(toTip).filter(t => t.body.trim().length > 0)
  } catch {
    return []
  }
}

/** 管理画面用。無効なものも含めて全部。★こちらは失敗を隠さない(管理者は原因を知る必要がある)。 */
export async function fetchAllTips() {
  const { data, error } = await supabase
    .from('app_tips')
    .select('tip_id, body, is_active, sort_order')
    .order('sort_order')
    .limit(TIPS_LIMIT)
  if (error) throw new Error(error.message)
  return (data ?? []).map(toTip)
}

export async function addTip({ body, sortOrder = 0 }) {
  const { data, error } = await supabase
    .from('app_tips')
    .insert({ body, sort_order: sortOrder, is_active: true })
    .select()
    .single()
  if (error) throw new Error(error.message)
  return toTip(data)
}

export async function updateTip(tipId, patch) {
  const row = {}
  if (patch?.body !== undefined) row.body = patch.body
  if (patch?.sortOrder !== undefined) row.sort_order = patch.sortOrder
  if (patch?.isActive !== undefined) row.is_active = patch.isActive
  row.updated_at = new Date().toISOString()
  const { data, error } = await supabase
    .from('app_tips')
    .update(row)
    .eq('tip_id', tipId)
    .select()
    .single()
  if (error) throw new Error(error.message)
  return toTip(data)
}

// ── SPEC-TIPS-USAGE-COUNT-V1 (D-766): どの Tip がどれだけ画面に出たか ─────────────
//   ★Tips を見るのは「まだログインしていない端末」= anon。既存の page_usage_stats は
//     staff_id + organization_id を持つログイン済み前提の表なので、そこには乗れない。
//     乗るのは「表」ではなく「作法」: SECURITY DEFINER 関数 1 本 + 累積カウンタ + fire-and-forget。

/**
 * 「この Tip が 1 回画面に出た」を記録する。
 *
 * ★fire-and-forget。await しない・例外を投げない・戻り値を見ない(D-524 trackFeatureUse と同じ形)。
 *   D-661 の最優先は「更新作業の邪魔をしない」。計測が更新導線を止めてはいけない。
 * ★加算量はここから渡さない。関数の中で固定 +1(匿名から呼べる関数に数値を渡させない)。
 * ★ログも出さない。D-661 の Tips 経路は「取れなければ無音」で通している(fetchActiveTips と同じ)。
 *   trackPageUsage の console.warn をここで真似ると、更新待ちの画面で 10 秒ごとに警告が出る。
 */
export function trackTipShown(tipId) {
  if (!tipId) return
  try {
    Promise.resolve(supabase.rpc('fn_track_tip_shown', { p_tip_id: tipId })).catch(() => {})
  } catch {
    /* 計測は更新作業を止めない */
  }
}

/**
 * 管理画面用。tip_id → { shownCount, lastShownAt }。
 *   ★失敗時は {}(既存 fetchMyPageUsage / D-068 と同じ作法)。集計は添え物なので、
 *     取れなくても Tips の一覧・編集をブロックしない。
 *   ★行が無い Tip は key ごと現れない。0 で埋めない(未計測と 0 回は違う = D-657 / D-492)。
 */
export async function fetchTipUsage() {
  try {
    const { data, error } = await supabase
      .from('app_tip_usage_stats')
      .select('tip_id, shown_count, last_shown_at')
      .order('tip_id')
      .limit(TIPS_LIMIT)
    if (error) return {}
    return Object.fromEntries(
      (data ?? []).map(r => [r.tip_id, {
        shownCount: Number.isFinite(Number(r?.shown_count)) ? Number(r.shown_count) : 0,
        lastShownAt: r?.last_shown_at ?? null,
      }])
    )
  } catch {
    return {}
  }
}
